import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import {
  useCreateStockTransferMutation,
  useGetLocationsQuery,
  useGetStockLevelsQuery,
  useLazyGetProductBatchesQuery
} from '../../../store/api/inventoryApi';
import { useGetProductsQuery } from '../../../store/api/productsApi';

const calculateFIFOCost = (batches, quantity, defaultCost) => {
  let remaining = parseFloat(quantity) || 0;
  let totalCost = 0;
  
  if (batches && batches.length > 0) {
    // Sort batches by receivedDate and createdAt ascending to make sure we use FIFO
    const sortedBatches = [...batches].sort((a, b) => {
      const dateA = new Date(a.receivedDate || a.createdAt);
      const dateB = new Date(b.receivedDate || b.createdAt);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

    for (const batch of sortedBatches) {
      if (remaining <= 0) break;
      const available = parseFloat(batch.quantity) || 0;
      if (available <= 0) continue;
      
      const take = Math.min(available, remaining);
      totalCost += take * parseFloat(batch.unitCost || 0);
      remaining -= take;
    }
  }
  
  // If there is still quantity remaining (or no batches found), fall back to defaultCost
  if (remaining > 0) {
    totalCost += remaining * defaultCost;
  }
  
  const total = Math.round((totalCost + Number.EPSILON) * 100) / 100;
  const unit = quantity > 0 ? Math.round(((totalCost / quantity) + Number.EPSILON) * 100) / 100 : defaultCost;
  
  return { unitCost: unit, totalCost: total };
};

function StockTransferForm() {
  const navigate = useNavigate();
  const [fromLocationId, setFromLocationId] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [transferDate, setTransferDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([{ productId: '', quantity: 1, productName: '', unitCost: 0, totalCost: 0, batchId: '', batchNo: '' }]);

  const { data: locations } = useGetLocationsQuery();
  const { data: productsData } = useGetProductsQuery({ limit: 1000 });
  const [createTransfer, { isLoading: isSaving }] = useCreateStockTransferMutation();
  const [triggerFetchBatches] = useLazyGetProductBatchesQuery();

  // Get stock levels for fromLocation to validate/show available qty
  const { data: stockLevelsData } = useGetStockLevelsQuery(
    { locationId: fromLocationId, limit: 1000 },
    { skip: !fromLocationId }
  );

  const products = productsData?.products || [];
  const stockLevels = stockLevelsData?.stockLevels || [];

  const totalTransferAmount = items.reduce((sum, item) => sum + (item.totalCost || 0), 0);

  // Automatically update unitCost and batches based on stock levels (FIFO average cost) when source location changes
  useEffect(() => {
    const fetchAllBatches = async () => {
      if (!fromLocationId || items.length === 0) return;
      
      const updatedItems = await Promise.all(
        items.map(async (item) => {
          if (!item.productId) return item;
          
          try {
            const batches = await triggerFetchBatches({
              productId: item.productId,
              locationId: fromLocationId,
            }).unwrap();
            
            const product = products.find((p) => p.id === item.productId);
            const factor = parseFloat(product?.conversionFactor) || 1;
            const defaultCost = (product?.costPrice || 0) / factor;
            
            let finalUnitCost = item.unitCost;
            let finalTotalCost = item.totalCost;
            if (!item.batchId) {
              const { unitCost, totalCost } = calculateFIFOCost(batches, item.quantity, defaultCost);
              finalUnitCost = unitCost;
              finalTotalCost = totalCost;
            }
            
            return {
              ...item,
              batches,
              defaultCost,
              unitCost: finalUnitCost,
              totalCost: finalTotalCost,
            };
          } catch (err) {
            console.error('Error fetching batches for location change:', err);
            return item;
          }
        })
      );
      
      setItems(updatedItems);
    };
    
    fetchAllBatches();
  }, [fromLocationId, triggerFetchBatches]);


  const handleAddItem = () => {
    setItems([...items, { productId: '', quantity: 1, productName: '', unitCost: 0, totalCost: 0, batchId: '', batchNo: '' }]);
  };

  const handleRemoveItem = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const handleItemChange = async (index, field, value) => {
    const newItems = [...items];
    if (field === 'product') {
      const productId = value?.id || '';
      newItems[index].productId = productId;
      newItems[index].productName = value?.name || '';
      newItems[index].batches = [];
      newItems[index].batchId = '';
      newItems[index].batchNo = '';
      
      const factor = parseFloat(value?.conversionFactor) || 1;
      const defaultCost = (value?.costPrice || 0) / factor;
      newItems[index].defaultCost = defaultCost;

      if (productId && fromLocationId) {
        try {
          const batches = await triggerFetchBatches({ productId, locationId: fromLocationId }).unwrap();
          newItems[index].batches = batches;
          const { unitCost, totalCost } = calculateFIFOCost(batches, newItems[index].quantity, defaultCost);
          newItems[index].unitCost = unitCost;
          newItems[index].totalCost = totalCost;
        } catch (err) {
          console.error('Failed to fetch product batches:', err);
          newItems[index].unitCost = defaultCost;
          newItems[index].totalCost = (newItems[index].quantity || 0) * defaultCost;
        }
      } else {
        newItems[index].unitCost = defaultCost;
        newItems[index].totalCost = (newItems[index].quantity || 0) * defaultCost;
      }
    } else if (field === 'batch') {
      const selectedBatch = value; // value is a batch object or null
      newItems[index].batchId = selectedBatch?.id || '';
      newItems[index].batchNo = selectedBatch?.batchNo || '';
      
      if (selectedBatch) {
        newItems[index].unitCost = parseFloat(selectedBatch.unitCost) || 0;
        const batchQty = parseFloat(selectedBatch.quantity) || 0;
        if (newItems[index].quantity > batchQty) {
          newItems[index].quantity = batchQty;
        }
        newItems[index].totalCost = newItems[index].quantity * newItems[index].unitCost;
      } else {
        const { unitCost, totalCost } = calculateFIFOCost(
          newItems[index].batches,
          newItems[index].quantity,
          newItems[index].defaultCost || 0
        );
        newItems[index].unitCost = unitCost;
        newItems[index].totalCost = totalCost;
      }
    } else if (field === 'quantity') {
      const qty = parseFloat(value) || 0;
      newItems[index].quantity = qty;
      
      if (newItems[index].batchId) {
        newItems[index].totalCost = qty * (newItems[index].unitCost || 0);
      } else {
        const { unitCost, totalCost } = calculateFIFOCost(
          newItems[index].batches,
          qty,
          newItems[index].defaultCost || 0
        );
        newItems[index].unitCost = unitCost;
        newItems[index].totalCost = totalCost;
      }
    } else if (field === 'unitCost') {
      const cost = parseFloat(value) || 0;
      newItems[index].unitCost = cost;
      newItems[index].totalCost = (newItems[index].quantity || 0) * cost;
    } else {
      newItems[index][field] = value;
    }
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fromLocationId || !toLocationId || items.some(item => !item.productId || item.quantity <= 0)) {
      alert('Please fill all required fields correctly');
      return;
    }

    try {
      await createTransfer({
        fromLocationId,
        toLocationId,
        transferDate,
        notes,
        totalAmount: totalTransferAmount,
        items: items.map(i => ({
          productId: i.productId,
          quantity: i.quantity,
          unitCost: i.unitCost,
          totalCost: i.totalCost,
          batchId: i.batchId || undefined,
          batchNo: i.batchNo || undefined
        })),
      }).unwrap();
      alert('Stock transfer completed successfully');
      navigate('/inventory/transfers');
    } catch (error) {
      alert(error?.data?.message || 'Error creating transfer');
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
        <IconButton onClick={() => navigate('/inventory/transfers')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          New Stock Transfer
        </Typography>
      </Box>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                required
                select
                label="From Location (Source)"
                value={fromLocationId}
                onChange={(e) => setFromLocationId(e.target.value)}
              >
                {locations?.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                required
                select
                label="To Location (Destination)"
                value={toLocationId}
                onChange={(e) => setToLocationId(e.target.value)}
              >
                {locations?.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                required
                type="date"
                label="Transfer Date"
                value={transferDate}
                onChange={(e) => setTransferDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Items to Transfer</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Batch</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Available Stock</TableCell>
                  <TableCell width={120}>Unit Cost</TableCell>
                  <TableCell width={120}>Quantity</TableCell>
                  <TableCell align="right" width={120}>Total Cost</TableCell>
                  <TableCell align="right" width={50}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => {
                  const stock = stockLevels.find(s => s.productId === item.productId);
                  const available = stock ? parseFloat(stock.quantity) - parseFloat(stock.reservedQuantity) : 0;

                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <Autocomplete
                          options={products}
                          getOptionLabel={(option) => `${option.name} (${option.sku})`}
                          value={products.find(p => p.id === item.productId) || null}
                          onChange={(_, newValue) => handleItemChange(index, 'product', newValue)}
                          renderInput={(params) => <TextField {...params} label="Select Product" required size="small" />}
                        />
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          options={item.batches || []}
                          getOptionLabel={(option) => option.batchNo ? `${option.batchNo} (Qty: ${option.quantity}, Cost: ${option.unitCost})` : `Batch ${option.id?.substring(0,6)} (Qty: ${option.quantity}, Cost: ${option.unitCost})`}
                          value={(item.batches || []).find(b => b.id === item.batchId) || null}
                          onChange={(_, newValue) => handleItemChange(index, 'batch', newValue)}
                          renderInput={(params) => <TextField {...params} label="Select Batch (Optional)" size="small" />}
                          disabled={!item.productId || (item.batches || []).length === 0}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {products.find(p => p.id === item.productId)?.baseUnit || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography color={available <= 0 ? 'error.main' : 'text.primary'}>
                          {available}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          fullWidth
                          size="small"
                          value={item.unitCost}
                          onChange={(e) => handleItemChange(index, 'unitCost', parseFloat(e.target.value))}
                          inputProps={{ min: 0, step: 0.01 }}
                          required
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          fullWidth
                          size="small"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value))}
                          inputProps={{ min: 0.01, step: 0.01 }}
                          required
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {(item.totalCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton color="error" onClick={() => handleRemoveItem(index)} disabled={items.length === 1}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddItem}
            >
              Add Item
            </Button>
            <Box sx={{ display: 'flex', gap: 4, textAlign: 'right' }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Total Items</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {items.length}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Total Qty</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Total Value</Typography>
                <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
                  {totalTransferAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate('/inventory/transfers')}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={isSaving}
        >
          {isSaving ? 'Processing...' : 'Complete Transfer'}
        </Button>
      </Box>
    </Box>
  );
}

export default StockTransferForm;
